import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RecordHeader } from "@/components/app/patterns/record-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { DeskFacts } from "./-components/desk-facts";

export const Route = createFileRoute("/_authenticated/_support/support-desk/$requestId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-console"),
  component: DeskRequestPage,
});

function DeskRequestPage() {
  const { requestId } = Route.useParams();
  const canManage = useCan("platform:manage");
  const queryClient = useQueryClient();
  const request = useQuery(orpc.support.requests.details.queryOptions({ input: { requestId } }));
  const resolve = useMutation(
    orpc.support.requests.resolve.mutationOptions({
      onSuccess: async (saved) => {
        toast.success(
          `${saved.reference} ${saved.status === "resolved" ? "resolved" : "reopened"}`,
        );
        await queryClient.invalidateQueries({ queryKey: orpc.support.requests.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const back = (
    <Link
      to="/support-desk"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
    >
      <ArrowLeft className="size-4" /> Support desk
    </Link>
  );
  if (request.isPending) {
    return (
      <>
        {back}
        <Skeleton className="h-64 w-full" />
      </>
    );
  }
  if (request.isError) {
    return (
      <>
        {back}
        <ReadFailed
          message="Couldn't load this request."
          error={request.error}
          onRetry={() => void request.refetch()}
          retrying={request.isRefetching}
        />
      </>
    );
  }

  const data = request.data;
  const resolved = data.status === "resolved";
  return (
    <>
      <RecordHeader
        back={{ label: "Support desk", link: { to: "/support-desk" } }}
        title={data.reference}
        badges={<Badge variant={resolved ? "ok" : "info"}>{resolved ? "Resolved" : "Open"}</Badge>}
        actions={
          canManage ? (
            <Button
              variant={resolved ? "outline" : "default"}
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ requestId, resolved: !resolved })}
            >
              {resolved ? "Reopen request" : "Mark resolved"}
            </Button>
          ) : null
        }
        strip={<DeskFacts request={data} />}
      />
      <Card>
        <CardHeader>
          <CardTitle>What they said</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm break-words whitespace-pre-line">{data.message}</p>
          {data.screenshotUrl ? (
            <a href={data.screenshotUrl} target="_blank" rel="noreferrer" className="block w-fit">
              <img
                src={data.screenshotUrl}
                alt={`Screenshot attached to ${data.reference}`}
                className="max-h-[32rem] rounded-md border"
              />
            </a>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
